import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  ListItemIcon,
  Snackbar,
  Tooltip,
  Stack,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Inventory2Outlined as StorageTabIcon,
  LocalShipping as PaymentTabIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { Category } from '../types/foundObject';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { useConversationDetailQuery } from '../hooks/useConversationDetailQuery';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { invalidateMessagingCaches, invalidateFoundObjectStats } from '../lib/messagingQueryCache';
import { getAnnouncementImageUrl, getAnnouncementThumbUrl } from '../utils/imageUrl';
import { extractEtiquetteUrlFromContent } from '../utils/colissimoLabel';
import { isDeliveryPaymentMessage } from '../utils/colissimoPaymentMessage';
import { isDeliveryRestitutionElsewhereMessage } from '../utils/deliveryRestitutionElsewhereMessage';
import { formatAttributeLabel } from '../utils/attributeLabels';
import {
  formatAttributeValueForCard,
  type CategoryAttributeDef,
} from '../utils/objectAttributeDisplay';
import { isSecretObjectAttributeKey, SECRET_OBJECT_ATTRIBUTE_KEY } from '../utils/secretObjectAttributes';
import ChooseDeliveryCarrierPanel from './ChooseDeliveryCarrierPanel';
import {
  type DeliveryCarrier,
  getAvailableDeliveryCarriers,
  getDeliveryCarrierDefaultAmountCents,
  formatAmountEur,
} from '../utils/deliveryPricing';
import { useDeliveryPricing } from '../hooks/useDeliveryPricing';
import {
  buildDeliveryOfferMessage,
  centsToEuroInput,
  euroInputToCents,
  isDeliveryOfferMessage,
  MAX_OFFER_AMOUNT_CENTS,
  MIN_OFFER_AMOUNT_CENTS,
} from '../utils/deliveryOfferMessage';
import CategoryIcon from './CategoryIcon';
import FormattedReferenceCode from './FormattedReferenceCode';
import {
  ConversationAvatar,
  ConversationMessageList,
  getConversationListButtonSx,
  IMAGE_MESSAGE_PREFIX,
  CONVERSATION_CHAT_BACKGROUND,
} from './conversation/ConversationChatUi';
import { getConversationTheme } from '../utils/conversationTheme';
import { DASHBOARD_BRAND_PRIMARY } from '../theme/dashboardBrand';
import { isCentralPlaceStorageMode, isSplitPlaceStorageMode } from '../utils/placeStorageMode';

/** Restitution en main propre — accent vert (maquette). */
const HANDOVER_DIALOG_GREEN = '#2e7d32';
const HANDOVER_DIALOG_GREEN_SOFT = 'rgba(46, 125, 50, 0.12)';

function firstImageUrl(images?: string[] | null): string | null {
  const p = images?.find((x) => x && String(x).trim());
  return p ? getAnnouncementImageUrl(p) : null;
}

const LOCATION_META_KEYS = new Set([
  'room',
  'zone',
  'saison',
  'etage',
  'storage_box_id',
  'provider_item_id',
  'found_provider_item_id',
  'storageBoxId',
  'providerItemId',
  'foundProviderItemId',
]);

function isLikelySecretAttributeKey(key: string): boolean {
  if (LOCATION_META_KEYS.has(key)) return false;
  return isSecretObjectAttributeKey(key);
}

function ObjectPlaceStorageSummary({
  placeName,
  boxName,
}: {
  placeName: string;
  boxName: string;
}) {
  if (!placeName && !boxName) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      {placeName ? (
        <Box sx={{ mb: boxName ? 0.75 : 0 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Lieu
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {placeName}
          </Typography>
        </Box>
      ) : null}
      {boxName ? (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Boîte de stockage
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {boxName}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export interface FoundObjectMessagesWorkspaceProps {
  foundObjectId: number;
  selectedConversationId: number | null;
  /** Passer `null` pour désélectionner (ex. retour liste sur mobile). */
  onSelectConversation: (id: number | null) => void;
  onHandoverConfirmed?: (userName: string) => void;
  /** Flux ouverture depuis une annonce (page Messages) */
  announcementFlow?: boolean;
  conversationPresentInUrl?: boolean;
  startingConversation?: boolean;
  startError?: string | null;
  onDismissStartError?: () => void;
  /** Fiche objet plein écran (page Objets trouvés) */
  variant?: 'default' | 'fullscreen';
  onClose?: () => void;
  /** Onglet « Modifier » : ouvre le formulaire d’édition (parent) */
  onRequestEditFoundObject?: () => void;
  /** Ouvre directement la liste des conversations archivées. */
  preferArchivedList?: boolean;
  /** Ouvre directement la liste restitués / livraisons. */
  preferRestoredList?: boolean;
}

const FoundObjectMessagesWorkspace: React.FC<FoundObjectMessagesWorkspaceProps> = ({
  foundObjectId,
  selectedConversationId,
  onSelectConversation,
  onHandoverConfirmed,
  announcementFlow = false,
  conversationPresentInUrl = false,
  startingConversation = false,
  startError = null,
  onDismissStartError,
  variant = 'default',
  onClose,
  onRequestEditFoundObject,
  preferArchivedList = false,
  preferRestoredList = false,
}) => {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatImageInputRef = useRef<HTMLInputElement | null>(null);
  const [composeText, setComposeText] = useState('');
  const [sendTextError, setSendTextError] = useState<string | null>(null);
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [actionRunning, setActionRunning] = useState(false);
  const [confirmUndoOpen, setConfirmUndoOpen] = useState(false);
  const [confirmHandoverOpen, setConfirmHandoverOpen] = useState(false);
  const [handoverEmail, setHandoverEmail] = useState('');
  const [deliveryOfferDialogOpen, setDeliveryOfferDialogOpen] = useState(false);
  const [deliveryOfferCarrier, setDeliveryOfferCarrier] = useState<DeliveryCarrier | null>(null);
  const [deliveryOfferAmountEur, setDeliveryOfferAmountEur] = useState('');
  const [deliveryOfferAmountError, setDeliveryOfferAmountError] = useState<string | null>(null);
  const [detailMainTab, setDetailMainTab] = useState(0);
  const [objectPhotoFullscreen, setObjectPhotoFullscreen] = useState(false);
  const [showArchivedConversations, setShowArchivedConversations] = useState(preferArchivedList);
  const [showRestoredConversations, setShowRestoredConversations] = useState(preferRestoredList);
  const [conversationMenuAnchor, setConversationMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionSnackbar, setActionSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const autoToShipDoneRef = useRef<number | null>(null);
  const isFullscreen = variant === 'fullscreen';
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMdDown && detailMainTab !== 0) {
      setDetailMainTab(0);
    }
  }, [isMdDown, detailMainTab]);

  useEffect(() => {
    if (preferArchivedList) {
      setShowArchivedConversations(true);
      setShowRestoredConversations(false);
    }
  }, [preferArchivedList]);

  useEffect(() => {
    if (preferRestoredList) {
      setShowRestoredConversations(true);
      setShowArchivedConversations(false);
    }
  }, [preferRestoredList]);

  const { data: foundObjectData, isLoading: loadingFoundObject } = useQuery({
    queryKey: ['found-object', foundObjectId],
    queryFn: () => apiService.getFoundObject(foundObjectId).then((r) => r.data.data),
    enabled: !!foundObjectId && !Number.isNaN(foundObjectId),
  });

  useEffect(() => {
    const status = foundObjectData?.status;
    if (status === 'claimed' || status === 'to_ship' || status === 'in_transit') {
      setShowRestoredConversations(true);
      setShowArchivedConversations(false);
    }
  }, [foundObjectData?.status]);

  const { data: convData, isLoading: loadingList } = useConversationsListQuery({
    poll: false,
    archived: showArchivedConversations,
    restored: !showArchivedConversations && showRestoredConversations,
  });

  const {
    data: detailData,
    isLoading: loadingDetail,
    error: detailError,
  } = useConversationDetailQuery({
    conversationId: selectedConversationId,
    poll: !!selectedConversationId,
  });

  useEffect(() => {
    if (!selectedConversationId) return;
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      void apiService.pingConversationViewing(selectedConversationId, true).catch(() => undefined);
    };
    ping();
    const intervalId = window.setInterval(ping, 25_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      void apiService.pingConversationViewing(selectedConversationId, false).catch(() => undefined);
    };
  }, [selectedConversationId]);

  useEffect(() => {
    if (detailData?.conversation?.is_archived === true) {
      setShowArchivedConversations(true);
      setShowRestoredConversations(false);
    }
  }, [detailData?.conversation?.id, detailData?.conversation?.is_archived]);

  const archiveMutation = useMutation({
    mutationFn: (id: number) =>
      showArchivedConversations
        ? apiService.unarchiveConversation(id)
        : apiService.archiveConversation(id),
    onSuccess: (res) => {
      setConversationMenuAnchor(null);
      onSelectConversation(null);
      if (showArchivedConversations) {
        setShowArchivedConversations(false);
      }
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setConversationMenuAnchor(null);
      setActionSnackbar({
        open: true,
        message: showArchivedConversations
          ? 'Impossible de désarchiver la conversation.'
          : 'Impossible d’archiver la conversation.',
        severity: 'error',
      });
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: (id: number) => apiService.markConversationUnread(id),
    onSuccess: (res) => {
      setConversationMenuAnchor(null);
      onSelectConversation(null);
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setConversationMenuAnchor(null);
      setActionSnackbar({
        open: true,
        message: 'Impossible de marquer la conversation comme non lue.',
        severity: 'error',
      });
    },
  });

  const { data: categoriesList } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => (response.data?.categories || []) as Category[],
    staleTime: 60_000,
  });

  const categoryForObject = useMemo(
    () => categoriesList?.find((c) => c.name === foundObjectData?.objectType),
    [categoriesList, foundObjectData?.objectType]
  );

  const { data: providerInfoData } = useProviderInfo();

  const objectPlaceAndStorage = useMemo(() => {
    if (!foundObjectData) return { placeName: '', boxName: '' };
    const boxName = foundObjectData.storage_box?.name?.trim() || '';
    const foundPlaceName =
      foundObjectData.found_provider_item?.display_name?.trim() ||
      foundObjectData.found_provider_item?.name?.trim() ||
      '';
    const storagePlaceName = foundObjectData.storage_box?.provider_item?.name?.trim() || '';
    const preferStoragePlace =
      !isSplitPlaceStorageMode(providerInfoData) && !isCentralPlaceStorageMode(providerInfoData);
    const placeName = preferStoragePlace
      ? storagePlaceName || foundPlaceName
      : foundPlaceName || storagePlaceName;
    return { placeName, boxName };
  }, [foundObjectData, providerInfoData]);

  const sendMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) => apiService.sendMessage(id, content),
    onSuccess: () => {
      setSendTextError(null);
      setComposeText('');
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] });
      void invalidateMessagingCaches(queryClient);
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string; code?: string } } };
      const code = ax.response?.data?.code;
      const apiError = ax.response?.data?.error;
      if (code === 'moderation_rejected' && typeof apiError === 'string' && apiError.trim()) {
        setSendTextError(apiError.trim());
        return;
      }
      setSendTextError(
        (typeof apiError === 'string' && apiError.trim()) ||
          "Impossible d'envoyer le message pour le moment."
      );
    },
  });

  const editMessageMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: number; content: string }) => {
      if (!selectedConversationId) {
        return Promise.reject(new Error('Conversation manquante'));
      }
      return apiService.updateConversationMessage(selectedConversationId, messageId, content);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] });
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: 'Message modifié.', severity: 'success' });
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string } } };
      const apiError = ax.response?.data?.error;
      setActionSnackbar({
        open: true,
        message:
          (typeof apiError === 'string' && apiError.trim()) || 'Impossible de modifier le message.',
        severity: 'error',
      });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: number) => {
      if (!selectedConversationId) {
        return Promise.reject(new Error('Conversation manquante'));
      }
      return apiService.deleteConversationMessage(selectedConversationId, messageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] });
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: 'Message supprimé.', severity: 'success' });
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string } } };
      const apiError = ax.response?.data?.error;
      setActionSnackbar({
        open: true,
        message:
          (typeof apiError === 'string' && apiError.trim()) || 'Impossible de supprimer le message.',
        severity: 'error',
      });
    },
  });

  const handleEditMessage = async (messageId: number, content: string) => {
    await editMessageMutation.mutateAsync({ messageId, content });
  };

  const handleDeleteMessage = async (messageId: number) => {
    await deleteMessageMutation.mutateAsync(messageId);
  };

  const messageActionsPending = editMessageMutation.isPending || deleteMessageMutation.isPending;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detailData?.messages?.length]);

  useEffect(() => {
    setImageUploadError(null);
    setSendTextError(null);
  }, [selectedConversationId]);

  const handleChatImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedConversationId || !canSendMessages) return;
    setImageUploadError(null);
    setImageUploadPending(true);
    try {
      const uploadRes = await apiService.uploadImage(file, 'messages');
      const uploaded = uploadRes.data;
      const uploadedPath =
        (uploaded.path && String(uploaded.path).trim()) ||
        (uploaded.url && String(uploaded.url).trim()) ||
        (uploaded.filename ? `/uploads/messages/${uploaded.filename}` : '');
      if (!uploadedPath) {
        throw new Error('Réponse serveur inattendue après upload.');
      }
      const imageMessage = `${IMAGE_MESSAGE_PREFIX}${uploadedPath}`;
      await sendMutation.mutateAsync({ id: selectedConversationId, content: imageMessage });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
      if (ax?.response?.data?.code === 'moderation_rejected') {
        const apiError = ax.response?.data?.error;
        setImageUploadError(
          typeof apiError === 'string' && apiError.trim()
            ? apiError.trim()
            : "Ce contenu n'a pas été accepté par la modération automatique."
        );
      } else {
        const msg =
          ax?.response?.data?.error ||
          ax?.message ||
          "Impossible d'envoyer l'image. Vérifiez le format (max 5 Mo) et réessayez.";
        setImageUploadError(typeof msg === 'string' ? msg : "Impossible d'envoyer l'image.");
      }
    } finally {
      setImageUploadPending(false);
    }
  };

  const objectConversations = useMemo(() => {
    const conversations = convData?.conversations;
    if (!conversations?.length) return [];
    return conversations
      .filter((c: any) => {
        const attrs = c.announcement?.object_attributes;
        const fromAttrs = Number(attrs?.found_object_id);
        if (Number.isFinite(fromAttrs) && fromAttrs === foundObjectId) return true;
        const ref = String(c.announcement?.reference_code ?? '');
        return ref === `FO-CONTACT-${foundObjectId}`;
      })
      .sort((a: any, b: any) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime());
  }, [foundObjectId, convData?.conversations]);

  const openHandoverDialog = useCallback(() => {
    setHandoverEmail('');
    setConfirmHandoverOpen(true);
  }, []);

  const closeHandoverDialog = useCallback(() => {
    setConfirmHandoverOpen(false);
    setHandoverEmail('');
  }, []);

  const handleSend = () => {
    if (!selectedConversationId || !composeText.trim()) return;
    sendMutation.mutate({ id: selectedConversationId, content: composeText.trim() });
  };

  const handleQuickSend = async (content: string) => {
    if (!selectedConversationId || !content.trim()) return;
    await sendMutation.mutateAsync({ id: selectedConversationId, content: content.trim() });
  };

  const matchingInTrash = detailData?.conversation?.matching_in_trash;
  const messagingLockedReason = detailData?.conversation?.messaging_locked_reason ?? null;
  const isRestitutionElsewhereLocked = messagingLockedReason === 'restitution_elsewhere';
  const isMessagingLocked =
    isRestitutionElsewhereLocked ||
    detailData?.conversation?.announcement?.messaging_allowed === false ||
    (detailData?.conversation?.announcement?.status != null &&
      detailData.conversation.announcement.status !== 'active');
  const isClaimed = foundObjectData?.status === 'claimed';
  const isToShip = foundObjectData?.status === 'to_ship';
  const isInTransit = foundObjectData?.status === 'in_transit';
  const isDisposed = foundObjectData?.status === 'disposed';
  /** Restitué / éliminé : plus de fil à afficher. */
  const hideMessagingBecauseClaimed = isClaimed || isDisposed;
  /** À expédier / transit : actions restitution masquées ; chat autorisé avec le payeur. */
  const isShippingLocked = isToShip || isInTransit;

  const paidConversationId = useMemo(() => {
    if (!objectConversations.length) return null;

    const isSiblingClosedPreview = (c: any): boolean => {
      if (c.messaging_locked_reason === 'restitution_elsewhere') return true;
      const content = String(c.last_message?.content ?? '');
      if (isDeliveryRestitutionElsewhereMessage(content)) return true;
      // Aperçu API raccourci : « Sherlook — Cet objet a été restitué… »
      return /objet a été restitué/i.test(content);
    };

    const withEtiquette = objectConversations.find((c: any) => {
      const content = String(c.last_message?.content ?? '');
      return isDeliveryPaymentMessage(content) && !!extractEtiquetteUrlFromContent(content);
    });
    if (withEtiquette?.id) return withEtiquette.id as number;

    const withPayment = objectConversations.find((c: any) =>
      isDeliveryPaymentMessage(String(c.last_message?.content ?? ''))
    );
    if (withPayment?.id) return withPayment.id as number;

    const activeShippingThreads = objectConversations.filter((c: any) => !isSiblingClosedPreview(c));

    const claimantName = String(
      foundObjectData?.claimantName ?? foundObjectData?.claimant_name ?? ''
    )
      .trim()
      .toLowerCase();
    if (claimantName) {
      const byName = activeShippingThreads.find((c: any) => {
        const full = `${c.other_user?.first_name ?? ''} ${c.other_user?.last_name ?? ''}`
          .trim()
          .toLowerCase();
        return full === claimantName;
      });
      if (byName?.id) return byName.id as number;
    }

    if (activeShippingThreads.length === 1) return activeShippingThreads[0].id as number;

    return null;
  }, [
    foundObjectData?.claimantName,
    foundObjectData?.claimant_name,
    objectConversations,
  ]);

  const isPaidConversationSelected =
    !!paidConversationId && selectedConversationId === paidConversationId;

  useEffect(() => {
    if (!isShippingLocked || selectedConversationId || !paidConversationId) return;
    onSelectConversation(paidConversationId);
  }, [isShippingLocked, selectedConversationId, paidConversationId, onSelectConversation]);

  const shippingRecipient = useMemo(() => {
    const nameFromObject = String(
      foundObjectData?.claimantName ?? foundObjectData?.claimant_name ?? ''
    ).trim();
    const emailFromObject = String(
      foundObjectData?.claimantEmail ?? foundObjectData?.claimant_email ?? ''
    ).trim();
    const phoneFromObject = String(
      foundObjectData?.claimantPhone ?? foundObjectData?.claimant_phone ?? ''
    ).trim();

    const paidOtherUser =
      (paidConversationId
        ? objectConversations.find((c: any) => c.id === paidConversationId)?.other_user
        : null) ??
      (isPaidConversationSelected ? detailData?.conversation?.other_user : null);
    const nameFromPaidConversation = paidOtherUser
      ? `${paidOtherUser.first_name ?? ''} ${paidOtherUser.last_name ?? ''}`.trim()
      : '';

    let addressHint: string | null = null;
    // Adresse uniquement dans le fil du payeur (message de paiement).
    if (isPaidConversationSelected) {
      const messages = detailData?.messages ?? [];
      for (const m of [...messages].reverse()) {
        const content = typeof m.content === 'string' ? m.content : '';
        if (!isDeliveryPaymentMessage(content)) continue;
        const relay = content.match(/Livraison en point relais\s*:\s*(.+?)\./i);
        if (relay?.[1]) {
          addressHint = relay[1].trim();
          break;
        }
        const addr = content.match(/Adresse\s*:\s*(.+?)\./i);
        if (addr?.[1]) {
          addressHint = addr[1].trim();
          break;
        }
      }
    }

    return {
      name: nameFromObject || nameFromPaidConversation || null,
      email: emailFromObject || null,
      phone: phoneFromObject || null,
      addressHint,
    };
  }, [
    detailData?.conversation?.other_user,
    detailData?.messages,
    foundObjectData?.claimantEmail,
    foundObjectData?.claimantName,
    foundObjectData?.claimantPhone,
    foundObjectData?.claimant_email,
    foundObjectData?.claimant_name,
    foundObjectData?.claimant_phone,
    isPaidConversationSelected,
    objectConversations,
    paidConversationId,
  ]);

  const messagingClosedAlert = (
    <Alert severity="info" sx={{ mb: 2 }}>
      {isToShip ? (
        <>
          Cet objet est <strong>à expédier</strong>. Vous pouvez encore échanger avec la personne qui a
          payé la livraison.
        </>
      ) : isInTransit ? (
        <>
          Cet objet est <strong>parti en livraison</strong>. Vous pouvez encore échanger avec la
          personne qui a payé.
        </>
      ) : isDisposed ? (
        <>
          Cet objet a été <strong>éliminé</strong>. La messagerie n’est plus affichée.
        </>
      ) : (
        <>
          Cet objet a été <strong>restitué</strong>. L’historique des messages reste consultable en
          lecture seule.
        </>
      )}
    </Alert>
  );

  const shippingRecipientBlock =
    isToShip || isInTransit ? (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {isToShip ? 'À expédier à' : 'Destinataire'}
        </Typography>
        {shippingRecipient.name ? (
          <Typography variant="body1" fontWeight={600}>
            {shippingRecipient.name}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Destinataire non renseigné — consultez le bon de livraison.
          </Typography>
        )}
        {shippingRecipient.email ? (
          <Typography variant="body2" color="text.secondary">
            {shippingRecipient.email}
          </Typography>
        ) : null}
        {shippingRecipient.phone ? (
          <Typography variant="body2" color="text.secondary">
            {shippingRecipient.phone}
          </Typography>
        ) : null}
        {shippingRecipient.addressHint ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {shippingRecipient.addressHint}
          </Typography>
        ) : null}
      </Box>
    ) : null;

  const canSendMessages =
    !!selectedConversationId &&
    !matchingInTrash &&
    !isMessagingLocked &&
    !hideMessagingBecauseClaimed &&
    (!isShippingLocked || isPaidConversationSelected);

  /** Champ de saisie visible : hors restitution, et pendant livraison uniquement sur le fil du payeur. */
  const showMessageComposer =
    !hideMessagingBecauseClaimed &&
    !!selectedConversationId &&
    (!isShippingLocked || isPaidConversationSelected);

  /** Boutons Je valide / restitution main propre : pas pendant expédition. */
  const showRestitutionQuickActions = !hideMessagingBecauseClaimed && !isShippingLocked;

  const conversationRowSx = (conversationId: number, selected: boolean) => {
    const base = getConversationListButtonSx(conversationId, selected);
    const conversation = objectConversations.find((c: any) => c.id === conversationId);
    const siblingClosed =
      conversation?.messaging_locked_reason === 'restitution_elsewhere' ||
      isDeliveryRestitutionElsewhereMessage(String(conversation?.last_message?.content ?? '')) ||
      /objet a été restitué/i.test(String(conversation?.last_message?.content ?? ''));
    const muted =
      isShippingLocked &&
      ((paidConversationId != null && conversationId !== paidConversationId) ||
        (paidConversationId == null && siblingClosed));
    return {
      ...base,
      ...(muted
        ? {
            opacity: 0.42,
            filter: 'grayscale(0.55)',
            bgcolor: 'action.hover',
          }
        : {}),
    };
  };

  const chatMessages = (providerInfoData?.settings?.chat_messages || {}) as {
    validation_message?: string;
    rejection_message?: string;
    handover_message?: string;
    undo_handover_message?: string;
    delivery_voucher_message?: string;
  };
  const validationQuickMessage =
    (chatMessages.validation_message && chatMessages.validation_message.trim()) ||
    'Bonjour, votre description correspond. Vous pouvez venir récupérer votre objet.';
  const rejectionQuickMessage =
    (chatMessages.rejection_message && chatMessages.rejection_message.trim()) ||
    "Bonjour, les informations ne correspondent pas à l'objet trouvé.";
  const handoverQuickMessage =
    (chatMessages.handover_message && chatMessages.handover_message.trim()) ||
    'Objet restitué en main propre. Merci !';
  const undoHandoverQuickMessage =
    (chatMessages.undo_handover_message && chatMessages.undo_handover_message.trim()) ||
    'La restitution en main propre a été annulée.';
  const deliveryVoucherQuickMessage =
    (chatMessages.delivery_voucher_message && chatMessages.delivery_voucher_message.trim()) ||
    "Bon de livraison : d'accord pour un bon Colissimo/UPS/Mondial Relay. Le paiement se fera sur Stripe par le perdeur, et une fois payé je pourrai imprimer le bon.";

  const deliveryLocationProviderId =
    foundObjectData?.location_provider?.id ?? providerInfoData?.id ?? undefined;

  const { data: deliveryPricing } = useDeliveryPricing({
    locationProviderId: deliveryLocationProviderId,
  });

  const colissimoPaymentMessage = useMemo(() => {
    const list = detailData?.messages ?? [];
    const lastSuccessful = [...list]
      .reverse()
      .find(
        (m: any) =>
          typeof m.content === 'string' &&
          isDeliveryPaymentMessage(m.content) &&
          /Etiquette:\s*\S+/i.test(m.content)
      );
    if (lastSuccessful) return lastSuccessful;
    return [...list]
      .reverse()
      .find((m: any) => typeof m.content === 'string' && isDeliveryPaymentMessage(m.content));
  }, [detailData?.messages]);

  const colissimoEtiquetteUrl = colissimoPaymentMessage?.content
    ? extractEtiquetteUrlFromContent(colissimoPaymentMessage.content)
    : null;

  const deliveryOfferAlreadySent = useMemo(() => {
    const messages = detailData?.messages ?? [];
    return messages.some(
      (m: { content?: string; is_from_me?: boolean }) =>
        !!m.is_from_me &&
        typeof m.content === 'string' &&
        isDeliveryOfferMessage(m.content)
    );
  }, [detailData?.messages]);

  const handleDeliveryOfferAction = async () => {
    if (deliveryOfferAlreadySent) return;
    const carriers = getAvailableDeliveryCarriers(deliveryPricing);
    if (carriers.length === 0) return;
    const initialCarrier = carriers.length === 1 ? carriers[0] : null;
    setDeliveryOfferCarrier(initialCarrier);
    setDeliveryOfferAmountEur(
      centsToEuroInput(getDeliveryCarrierDefaultAmountCents(initialCarrier ?? carriers[0], deliveryPricing))
    );
    setDeliveryOfferAmountError(null);
    setDeliveryOfferDialogOpen(true);
  };

  const handleDeliveryOfferCarrierChange = (carrier: DeliveryCarrier | null) => {
    setDeliveryOfferCarrier(carrier);
    if (carrier) {
      setDeliveryOfferAmountEur(
        centsToEuroInput(getDeliveryCarrierDefaultAmountCents(carrier, deliveryPricing))
      );
      setDeliveryOfferAmountError(null);
    }
  };

  const sendDeliveryOffer = async () => {
    if (!deliveryOfferCarrier) return;
    const cents = euroInputToCents(deliveryOfferAmountEur);
    if (cents == null) {
      setDeliveryOfferAmountError(
        `Tarif invalide (entre ${formatAmountEur(MIN_OFFER_AMOUNT_CENTS / 100)} et ${formatAmountEur(MAX_OFFER_AMOUNT_CENTS / 100)}).`
      );
      return;
    }
    const customBody = deliveryVoucherQuickMessage.replace(/^Bon de livraison :\s*/i, '').trim();
    await handleQuickSend(buildDeliveryOfferMessage(deliveryOfferCarrier, customBody, cents));
    setDeliveryOfferDialogOpen(false);
  };

  const { publicAttrEntries, secretAttrEntries } = useMemo(() => {
    const raw = foundObjectData?.objectAttributes || {};
    const catAttrs = categoryForObject?.attributes;
    type AttrEntry = { key: string; text: string; colorHex?: string | null };
    const publicList: AttrEntry[] = [];
    const secretList: AttrEntry[] = [];
    for (const [key, val] of Object.entries(raw)) {
      if (LOCATION_META_KEYS.has(key)) continue;
      const str = val == null ? '' : String(val);
      if (!str.trim()) continue;
      const def = catAttrs?.[key] as CategoryAttributeDef | undefined;
      const formatted = formatAttributeValueForCard(val, def);
      const entry: AttrEntry = {
        key,
        text: formatted.text,
        colorHex: formatted.colorHex ?? null,
      };
      const isSecret =
        def?.secret_detail === true || (def == null && isLikelySecretAttributeKey(key));
      if (isSecret) secretList.push(entry);
      else publicList.push(entry);
    }
    return { publicAttrEntries: publicList, secretAttrEntries: secretList };
  }, [foundObjectData?.objectAttributes, categoryForObject?.attributes]);

  const objectImages =
    foundObjectData?.images || detailData?.conversation?.announcement?.images || [];
  const objectThumbUrl = getAnnouncementThumbUrl(
    objectImages,
    categoryForObject?.default_image_url,
  );
  const objectFullImageUrl = firstImageUrl(objectImages) || objectThumbUrl;

  const renderAttributeValue = (text: string, colorHex?: string | null) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
      {colorHex ? (
        <Box
          aria-hidden
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            border: '1px solid rgba(13, 40, 64, 0.2)',
            bgcolor: colorHex,
            flexShrink: 0,
          }}
        />
      ) : null}
      <Typography variant="body2" fontWeight={500} component="span">
        {text}
      </Typography>
    </Box>
  );

  const linkedAnnouncementSecretClues = useMemo(() => {
    const attrs = detailData?.conversation?.announcement?.object_attributes;
    if (!attrs || typeof attrs !== 'object') return '';
    const raw = (attrs as Record<string, unknown>)[SECRET_OBJECT_ATTRIBUTE_KEY];
    return raw == null ? '' : String(raw).trim();
  }, [detailData?.conversation?.announcement?.object_attributes]);

  const handleShareObjectLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?found_object=${foundObjectId}`;
    void (async () => {
      try {
        if (navigator.share) {
          await navigator.share({ title: foundObjectData?.title || 'Objet Sherlook', url });
          return;
        }
      } catch {
        /* annulé ou indisponible */
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* ignore */
      }
    })();
  };

  useEffect(() => {
    const objectId = foundObjectId;
    if (!objectId || objectId <= 0) return;
    if (!colissimoEtiquetteUrl) return;
    if (!foundObjectData?.status) return;
    if (
      foundObjectData.status === 'to_ship' ||
      foundObjectData.status === 'in_transit' ||
      foundObjectData.status === 'claimed'
    ) {
      return;
    }
    if (autoToShipDoneRef.current === objectId) return;

    autoToShipDoneRef.current = objectId;
    apiService
      .updateFoundObject(objectId, { status: 'to_ship' })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['found-object', objectId] });
        queryClient.invalidateQueries({ queryKey: ['found-objects'] });
        void invalidateFoundObjectStats(queryClient);
      })
      .catch(() => {
        autoToShipDoneRef.current = null;
      });
  }, [foundObjectId, colissimoEtiquetteUrl, foundObjectData?.status, queryClient]);

  const handleMarkClaimed = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      const currentUser = apiService.getCurrentUser();
      const actorName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim();
      const otherUser =
        (selectedConversationId && detailData?.conversation?.id === selectedConversationId
          ? detailData.conversation.other_user
          : null) ??
        (selectedConversationId
          ? objectConversations.find((c: any) => c.id === selectedConversationId)?.other_user
          : null);
      const recipientName = otherUser
        ? `${otherUser.first_name ?? ''} ${otherUser.last_name ?? ''}`.trim()
        : '';
      const updatePayload: Record<string, unknown> = { status: 'claimed' };
      if (recipientName) {
        updatePayload.claimant_name = recipientName;
      } else if (actorName) {
        updatePayload.claimant_name = actorName;
      }
      const trimmedEmail = handoverEmail.trim();
      if (trimmedEmail) updatePayload.claimant_email = trimmedEmail;
      await apiService.updateFoundObject(foundObjectId, updatePayload as any);
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
      if (selectedConversationId) {
        await handleQuickSend(handoverQuickMessage);
      }
      onHandoverConfirmed?.(actorName || 'Champion');
    } finally {
      setActionRunning(false);
    }
  };

  const handleUndoClaimed = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      await apiService.updateFoundObject(foundObjectId, { status: 'found' });
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
      if (selectedConversationId) {
        await handleQuickSend(undoHandoverQuickMessage);
      }
    } finally {
      setActionRunning(false);
      setConfirmUndoOpen(false);
    }
  };

  const handleMarkInTransit = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      await apiService.updateFoundObject(foundObjectId, { status: 'in_transit' });
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
    } finally {
      setActionRunning(false);
    }
  };

  const handleMarkToShip = async () => {
    if (!foundObjectId) return;
    setActionRunning(true);
    try {
      await apiService.updateFoundObject(foundObjectId, { status: 'to_ship' });
      await queryClient.invalidateQueries({ queryKey: ['found-object', foundObjectId] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await invalidateFoundObjectStats(queryClient);
    } finally {
      setActionRunning(false);
    }
  };

  const shippingReadOnlyBanner =
    isShippingLocked && !hideMessagingBecauseClaimed && isPaidConversationSelected ? (
      <Box sx={{ mb: 2 }}>
        {messagingClosedAlert}
        {shippingRecipientBlock}
        {colissimoEtiquetteUrl ? (
          <Button
            variant="contained"
            href={colissimoEtiquetteUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ mt: 1 }}
            onClick={() => {
              if (isToShip) {
                void handleMarkInTransit();
              }
            }}
          >
            Imprimer le bon
          </Button>
        ) : null}
      </Box>
    ) : null;

  const activeConversationTheme = useMemo(
    () => (selectedConversationId ? getConversationTheme(selectedConversationId) : null),
    [selectedConversationId]
  );

  return (
    <>
      {!isFullscreen ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            minHeight: { xs: 360, md: 480 },
            maxWidth: '100%',
            flex: 1,
            overflow: 'auto',
          }}
        >
      <Card sx={{ width: { xs: '100%', md: 320 }, maxWidth: '100%', flexShrink: 0 }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Objet
          </Typography>
          {loadingFoundObject || !foundObjectData ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Box
                component="img"
                src={
                  getAnnouncementThumbUrl(
                    foundObjectData?.images || detailData?.conversation?.announcement?.images || [],
                    undefined
                  ) || undefined
                }
                alt=""
                sx={{
                  width: '100%',
                  height: { xs: 140, sm: 180 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  mb: 1.5,
                }}
              />
              <Typography variant="h6">
                {foundObjectData?.title || detailData?.conversation?.announcement?.title || 'Objet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {detailData?.conversation?.announcement?.description || 'Aucun détail'}
              </Typography>
              {foundObjectData?.reference_code && (
                <FormattedReferenceCode
                  code={foundObjectData.reference_code}
                  labelPrefix="Ref: "
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      <Card
        sx={{
          width: { xs: '100%', md: 360 },
          maxWidth: '100%',
          flexShrink: 0,
          maxHeight: { xs: '36vh', md: '78vh' },
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Conversations ({objectConversations.length})
          </Typography>
          <Chip
            size="small"
            label={showArchivedConversations ? 'Archivées' : 'Actives'}
            color={showArchivedConversations ? 'secondary' : 'default'}
            variant={showArchivedConversations ? 'filled' : 'outlined'}
            onClick={() => {
              setShowArchivedConversations((v) => !v);
              onSelectConversation(null);
            }}
            clickable
          />
        </Box>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : objectConversations.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {showArchivedConversations
                ? 'Aucune conversation archivée pour cet objet.'
                : 'Aucune conversation pour cet objet.'}
            </Typography>
          </Box>
        ) : (
          <List dense>
            {objectConversations.map((c: any) => (
              <ListItem
                key={c.id}
                disablePadding
                secondaryAction={
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <Tooltip title="Marquer comme non lu">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Marquer comme non lu"
                          disabled={markUnreadMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            markUnreadMutation.mutate(c.id);
                          }}
                        >
                          <MarkEmailUnreadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={showArchivedConversations ? 'Désarchiver' : 'Archiver'}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={showArchivedConversations ? 'Désarchiver' : 'Archiver'}
                          disabled={archiveMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveMutation.mutate(c.id);
                          }}
                        >
                          {showArchivedConversations ? (
                            <UnarchiveIcon fontSize="small" />
                          ) : (
                            <ArchiveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedConversationId === c.id}
                  onClick={() => onSelectConversation(c.id)}
                  sx={{
                    ...conversationRowSx(c.id, selectedConversationId === c.id),
                    pr: 10,
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!c.unread_count || c.unread_count === 0}
                      overlap="circular"
                    >
                      <ConversationAvatar
                        conversationId={c.id}
                        imageSrc={firstImageUrl(c.announcement?.images)}
                        initial={c.other_user?.first_name}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <>
                        {c.other_user ? `${c.other_user.first_name} ${c.other_user.last_name}` : 'Utilisateur'}
                        {isShippingLocked && paidConversationId === c.id ? ' · Payé' : ''}
                      </>
                    }
                    secondary={c.last_message?.content?.slice(0, 60) || '…'}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      <Card
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 280, sm: 400 },
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        {announcementFlow && !conversationPresentInUrl && startingConversation && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Ouverture de la conversation…</Typography>
          </Box>
        )}

        {startError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={onDismissStartError}>
            {startError}
          </Alert>
        )}

        {!selectedConversationId && !announcementFlow && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Sélectionnez une conversation ou ouvrez-en une depuis Mes matchings.
            </Typography>
          </Box>
        )}

        {selectedConversationId && (
          <>
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              {loadingDetail ? (
                <CircularProgress size={24} />
              ) : detailError ? (
                <Alert severity="error">Conversation introuvable ou accès refusé.</Alert>
              ) : (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6">
                      {detailData?.conversation?.other_user
                        ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                        : 'Conversation'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {detailData?.conversation?.announcement?.title}
                    </Typography>
                    {matchingInTrash && (
                      <Chip
                        sx={{ mt: 1 }}
                        label="Matching en corbeille — messagerie limitée"
                        color="warning"
                        size="small"
                      />
                    )}
                    {isRestitutionElsewhereLocked && (
                      <Chip
                        sx={{ mt: 1 }}
                        label="Objet restitué via une autre conversation"
                        color="warning"
                        size="small"
                      />
                    )}
                    {showArchivedConversations && (
                      <Chip sx={{ mt: 1 }} label="Archivée" size="small" color="secondary" />
                    )}
                  </Box>
                  <Tooltip title="Actions">
                    <IconButton
                      size="small"
                      aria-label="Actions conversation"
                      onClick={(e) => setConversationMenuAnchor(e.currentTarget)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: CONVERSATION_CHAT_BACKGROUND }}>
              {hideMessagingBecauseClaimed ? (
                <Box>
                  {messagingClosedAlert}
                  {isClaimed && selectedConversationId && activeConversationTheme ? (
                    <>
                      {loadingDetail ? (
                        <CircularProgress />
                      ) : (
                        <ConversationMessageList
                          messages={detailData?.messages || []}
                          theme={activeConversationTheme}
                          deliveryPricing={deliveryPricing}
                          deliveryLocationProviderId={deliveryLocationProviderId}
                          foundObjectStatus={foundObjectData?.status ?? null}
                          onMarkInTransit={handleMarkInTransit}
                          onMarkToShip={handleMarkToShip}
                          statusActionPending={actionRunning}
                          onEditMessage={handleEditMessage}
                          onDeleteMessage={handleDeleteMessage}
                          messageActionsPending={messageActionsPending}
                        />
                      )}
                      <div ref={messagesEndRef} />
                    </>
                  ) : null}
                  {isClaimed ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Vous pouvez annuler la restitution ci-dessous si besoin.
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <>
              {shippingReadOnlyBanner}
              {isRestitutionElsewhereLocked && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Cet objet a été restitué à son propriétaire via une autre conversation. La messagerie est en lecture
                  seule.
                </Alert>
              )}
              {loadingDetail ? (
                <CircularProgress />
              ) : activeConversationTheme ? (
                <>
                  <ConversationMessageList
                    messages={detailData?.messages || []}
                    theme={activeConversationTheme}
                    deliveryPricing={deliveryPricing}
                    deliveryLocationProviderId={deliveryLocationProviderId}
                    foundObjectStatus={foundObjectData?.status ?? null}
                    onMarkInTransit={handleMarkInTransit}
                    onMarkToShip={handleMarkToShip}
                    statusActionPending={actionRunning}
                    onEditMessage={handleEditMessage}
                    onDeleteMessage={handleDeleteMessage}
                    messageActionsPending={messageActionsPending}
                  />
                  <div ref={messagesEndRef} />
                </>
              ) : null}
                </>
              )}
            </Box>

            {!hideMessagingBecauseClaimed && showMessageComposer ? (
            <>
            {imageUploadError && (
              <Alert severity="error" onClose={() => setImageUploadError(null)} sx={{ mx: 2 }}>
                {imageUploadError}
              </Alert>
            )}
            {sendTextError && (
              <Alert severity="warning" onClose={() => setSendTextError(null)} sx={{ mx: 2 }}>
                {sendTextError}
              </Alert>
            )}
            <Divider />
            <input
              ref={chatImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleChatImageSelected}
            />
            <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                color="primary"
                size="small"
                aria-label="Envoyer une photo"
                onClick={() => chatImageInputRef.current?.click()}
                disabled={!canSendMessages || imageUploadPending || sendMutation.isPending}
              >
                {imageUploadPending ? <CircularProgress size={22} /> : <PhotoCameraIcon />}
              </IconButton>
              <TextField
                fullWidth
                size="small"
                placeholder="Votre message…"
                value={composeText}
                onChange={(e) => {
                  setSendTextError(null);
                  setComposeText(e.target.value);
                }}
                disabled={!canSendMessages || sendMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!canSendMessages || !composeText.trim() || sendMutation.isPending}
              >
                <SendIcon />
              </IconButton>
            </Box>
            {showRestitutionQuickActions ? (
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={isClaimed ? () => setConfirmUndoOpen(true) : openHandoverDialog}
                disabled={
                  !foundObjectId ||
                  actionRunning ||
                  sendMutation.isPending
                }
              >
                {isClaimed ? 'Annuler la restitution' : 'Restitué en main propre'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleQuickSend(validationQuickMessage)}
                disabled={!canSendMessages || actionRunning || sendMutation.isPending}
              >
                Je valide
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleQuickSend(rejectionQuickMessage)}
                disabled={!canSendMessages || actionRunning || sendMutation.isPending}
              >
                Je ne valide pas
              </Button>
              <Button
                size="small"
                variant="contained"
                component={colissimoEtiquetteUrl ? 'a' : 'button'}
                href={colissimoEtiquetteUrl || undefined}
                target={colissimoEtiquetteUrl ? '_blank' : undefined}
                rel={colissimoEtiquetteUrl ? 'noreferrer' : undefined}
                onClick={colissimoEtiquetteUrl ? undefined : () => void handleDeliveryOfferAction()}
                disabled={
                  !canSendMessages ||
                  actionRunning ||
                  sendMutation.isPending ||
                  (deliveryOfferAlreadySent && !colissimoEtiquetteUrl)
                }
              >
                {colissimoEtiquetteUrl
                  ? 'Imprimer le bon'
                  : deliveryOfferAlreadySent
                    ? 'Bon de livraison envoyé'
                    : 'Bon de livraison'}
              </Button>
            </Box>
            ) : null}
            </>
            ) : isClaimed ? (
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => setConfirmUndoOpen(true)}
                disabled={!foundObjectId || actionRunning}
              >
                Annuler la restitution
              </Button>
            </Box>
            ) : null}
          </>
        )}

        {!selectedConversationId && !announcementFlow && !hideMessagingBecauseClaimed && !isShippingLocked && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={openHandoverDialog}
              disabled={!foundObjectId || actionRunning}
            >
              Restitué en main propre
            </Button>
            <Typography variant="caption" color="text.secondary">
              La restitution mettra a jour le statut de l'objet meme sans conversation.
            </Typography>
          </Box>
        )}
      </Card>
        </Box>
      ) : (
        <Box
          sx={{
            height: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {isMdDown && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                zIndex: 3,
              }}
            >
              {loadingFoundObject || !foundObjectData ? (
                <CircularProgress size={28} sx={{ ml: 0.5 }} />
              ) : (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormattedReferenceCode
                      code={
                        foundObjectData.reference_code
                          ? String(foundObjectData.reference_code)
                          : String(foundObjectData.id)
                      }
                      hashPrefix
                      stripFoPrefix
                      variant="subtitle2"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      noWrap
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                      <CategoryIcon
                        icon={categoryForObject?.icon}
                        customIconUrl={categoryForObject?.custom_icon_url}
                        customIconFilename={categoryForObject?.custom_icon_filename}
                        size="small"
                        color="primary.main"
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {foundObjectData.objectType || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {new Date(foundObjectData.foundAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  {onClose && (
                    <IconButton edge="end" onClick={onClose} aria-label="Fermer" size="small">
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'row',
              overflow: 'hidden',
            }}
          >
          {!isMdDown && (
          <Box
            sx={{
              width: 300,
              maxWidth: 320,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflow: 'auto',
              bgcolor: 'grey.50',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              {onClose && (
                <IconButton edge="end" onClick={onClose} aria-label="Fermer" size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            {loadingFoundObject || !foundObjectData ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <Box
                  component="img"
                  src={
                    getAnnouncementThumbUrl(
                      foundObjectData?.images || detailData?.conversation?.announcement?.images || [],
                      categoryForObject?.default_image_url
                    ) || undefined
                  }
                  alt=""
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    bgcolor: 'grey.200',
                    mb: 1,
                  }}
                />
                <ObjectPlaceStorageSummary
                  placeName={objectPlaceAndStorage.placeName}
                  boxName={objectPlaceAndStorage.boxName}
                />
                <Chip
                  size="small"
                  label={foundObjectData.objectType || '—'}
                  sx={{ mb: 1.5, fontWeight: 600 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <CategoryIcon
                    icon={categoryForObject?.icon}
                    customIconUrl={categoryForObject?.custom_icon_url}
                    customIconFilename={categoryForObject?.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  <FormattedReferenceCode
                    code={
                      foundObjectData.reference_code
                        ? String(foundObjectData.reference_code)
                        : String(foundObjectData.id)
                    }
                    hashPrefix
                    stripFoPrefix
                    variant="subtitle1"
                    sx={{ fontWeight: 600 }}
                  />
                  <IconButton size="small" onClick={handleShareObjectLink} aria-label="Partager le lien">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(foundObjectData.foundAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Caractéristiques
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {publicAttrEntries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucune caractéristique renseignée.
                    </Typography>
                  ) : (
                    publicAttrEntries.map(({ key, text, colorHex }) => (
                      <Box
                        key={key}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                        </Typography>
                        {renderAttributeValue(text, colorHex)}
                      </Box>
                    ))
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Détails secrets
                </Typography>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Box sx={{ p: 1.5 }}>
                    {secretAttrEntries.length === 0 && !linkedAnnouncementSecretClues ? (
                      <Typography variant="body2" color="text.secondary">
                        Aucun détail secret renseigné.
                      </Typography>
                    ) : (
                      <>
                        {linkedAnnouncementSecretClues ? (
                          <Box sx={{ mb: secretAttrEntries.length > 0 ? 1.5 : 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Détail secret (déclarant)
                            </Typography>
                            <Typography variant="body2">{linkedAnnouncementSecretClues}</Typography>
                          </Box>
                        ) : null}
                        {secretAttrEntries.map(({ key, text, colorHex }) => (
                          <Box key={key} sx={{ mb: secretAttrEntries.length > 1 ? 1.5 : 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                            </Typography>
                            {renderAttributeValue(text, colorHex)}
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                </Card>
              </>
            )}
          </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {isMdDown && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  flexShrink: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, pr: 1 }}>
                    {objectThumbUrl ? (
                      <Box
                        component="img"
                        src={objectThumbUrl}
                        alt="Photo de l'objet"
                        onClick={(event) => {
                          event.stopPropagation();
                          setObjectPhotoFullscreen(true);
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          objectFit: 'cover',
                          bgcolor: 'grey.200',
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    ) : null}
                    <Typography variant="subtitle2" fontWeight={600}>
                      Fiche objet
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'grey.50', maxHeight: '40vh', overflow: 'auto' }}
                >
                  {loadingFoundObject || !foundObjectData ? (
                    <CircularProgress size={22} />
                  ) : (
                    <>
                      <ObjectPlaceStorageSummary
                        placeName={objectPlaceAndStorage.placeName}
                        boxName={objectPlaceAndStorage.boxName}
                      />
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Caractéristiques
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {publicAttrEntries.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucune caractéristique renseignée.
                          </Typography>
                        ) : (
                          publicAttrEntries.map(({ key, text, colorHex }) => (
                            <Box
                              key={key}
                              sx={{
                                p: 1.25,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                              </Typography>
                              {renderAttributeValue(text, colorHex)}
                            </Box>
                          ))
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Détails secrets
                      </Typography>
                      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Box sx={{ p: 1.5 }}>
                          {secretAttrEntries.length === 0 && !linkedAnnouncementSecretClues ? (
                            <Typography variant="body2" color="text.secondary">
                              Aucun détail secret renseigné.
                            </Typography>
                          ) : (
                            <>
                              {linkedAnnouncementSecretClues ? (
                                <Box sx={{ mb: secretAttrEntries.length > 0 ? 1.5 : 0 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Détail secret (déclarant)
                                  </Typography>
                                  <Typography variant="body2">{linkedAnnouncementSecretClues}</Typography>
                                </Box>
                              ) : null}
                              {secretAttrEntries.map(({ key, text, colorHex }) => (
                                <Box key={key} sx={{ mb: secretAttrEntries.length > 1 ? 1.5 : 0 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {formatAttributeLabel(key, {
                            categoryAttributes: categoryForObject?.attributes,
                            objectAttributes: foundObjectData?.objectAttributes,
                          })}
                                  </Typography>
                                  {renderAttributeValue(text, colorHex)}
                                </Box>
                              ))}
                            </>
                          )}
                        </Box>
                      </Card>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
            {!isMdDown && (
            <Tabs
              value={detailMainTab}
              onChange={(_, v) => setDetailMainTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  color: DASHBOARD_BRAND_PRIMARY,
                  opacity: 0.72,
                  '&.Mui-selected': {
                    color: DASHBOARD_BRAND_PRIMARY,
                    opacity: 1,
                  },
                },
                '& .MuiTabs-indicator': { bgcolor: DASHBOARD_BRAND_PRIMARY },
              }}
            >
              <Tab label="Discuter" icon={<ChatIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Modifier" icon={<EditIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Stockage" icon={<StorageTabIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Suivi de paiement" icon={<PaymentTabIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
            )}

            {(isMdDown || detailMainTab === 0) && (
              <>
                {hideMessagingBecauseClaimed ? (
                  <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                    {messagingClosedAlert}
                    {isClaimed && selectedConversationId ? (
                      loadingDetail ? (
                        <CircularProgress />
                      ) : activeConversationTheme ? (
                        <>
                          <ConversationMessageList
                            messages={detailData?.messages || []}
                            theme={activeConversationTheme}
                            deliveryPricing={deliveryPricing}
                            deliveryLocationProviderId={deliveryLocationProviderId}
                            foundObjectStatus={foundObjectData?.status ?? null}
                            onMarkInTransit={handleMarkInTransit}
                            onMarkToShip={handleMarkToShip}
                            statusActionPending={actionRunning}
                            onEditMessage={handleEditMessage}
                            onDeleteMessage={handleDeleteMessage}
                            messageActionsPending={messageActionsPending}
                          />
                          <div ref={messagesEndRef} />
                        </>
                      ) : null
                    ) : isClaimed ? (
                      <Typography variant="body2" color="text.secondary">
                        Sélectionnez une conversation pour consulter l’historique, ou utilisez l’onglet
                        Modifier.
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      width: { xs: '100%', md: 300 },
                      maxWidth: '100%',
                      flexShrink: 0,
                      borderRadius: 0,
                      borderRight: { md: 1 },
                      borderBottom: { xs: 1, md: 0 },
                      borderColor: 'divider',
                      display: {
                        xs: selectedConversationId ? 'none' : 'flex',
                        md: 'flex',
                      },
                      flexDirection: 'column',
                      minHeight: { xs: 0, md: 0 },
                      flex: { xs: selectedConversationId ? 0 : 1, md: 'unset' },
                      maxHeight: { xs: 'none', md: 'none' },
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Conversations ({objectConversations.length})
                        </Typography>
                        <Chip
                          size="small"
                          label={showArchivedConversations ? 'Archivées' : 'Actives'}
                          color={showArchivedConversations ? 'secondary' : 'default'}
                          variant={showArchivedConversations ? 'filled' : 'outlined'}
                          onClick={() => {
                            setShowArchivedConversations((v) => !v);
                            onSelectConversation(null);
                          }}
                          clickable
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="Actualiser"
                        onClick={() => void invalidateMessagingCaches(queryClient)}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      {loadingList ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : objectConversations.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {showArchivedConversations
                              ? 'Aucune conversation archivée pour cet objet.'
                              : 'Aucune conversation pour cet objet.'}
                          </Typography>
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {objectConversations.map((c: any) => (
                            <ListItem
                              key={c.id}
                              disablePadding
                              secondaryAction={
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                  <Tooltip title="Marquer comme non lu">
                                    <span>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        aria-label="Marquer comme non lu"
                                        disabled={markUnreadMutation.isPending}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markUnreadMutation.mutate(c.id);
                                        }}
                                      >
                                        <MarkEmailUnreadIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={showArchivedConversations ? 'Désarchiver' : 'Archiver'}>
                                    <span>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        aria-label={
                                          showArchivedConversations ? 'Désarchiver' : 'Archiver'
                                        }
                                        disabled={archiveMutation.isPending}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          archiveMutation.mutate(c.id);
                                        }}
                                      >
                                        {showArchivedConversations ? (
                                          <UnarchiveIcon fontSize="small" />
                                        ) : (
                                          <ArchiveIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              }
                            >
                              <ListItemButton
                                selected={selectedConversationId === c.id}
                                onClick={() => onSelectConversation(c.id)}
                                sx={{
                                  ...conversationRowSx(c.id, selectedConversationId === c.id),
                                  pr: 10,
                                }}
                              >
                                <ListItemAvatar>
                                  <Badge
                                    color="error"
                                    variant="dot"
                                    invisible={!c.unread_count || c.unread_count === 0}
                                    overlap="circular"
                                  >
                                    <ConversationAvatar
                                      conversationId={c.id}
                                      imageSrc={firstImageUrl(c.announcement?.images)}
                                      initial={c.other_user?.first_name}
                                    />
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <>
                                      {c.other_user?.email ||
                                        (c.other_user
                                          ? `${c.other_user.first_name} ${c.other_user.last_name}`
                                          : 'Utilisateur')}
                                      {isShippingLocked && paidConversationId === c.id ? ' · Payé' : ''}
                                    </>
                                  }
                                  secondary={c.last_message?.content?.slice(0, 60) || '…'}
                                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: {
                        xs: selectedConversationId ? 'flex' : 'none',
                        md: 'flex',
                      },
                      flexDirection: 'column',
                      minWidth: 0,
                      minHeight: 0,
                      borderRadius: 0,
                    }}
                  >
                    {announcementFlow && !conversationPresentInUrl && startingConversation && (
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Ouverture de la conversation…</Typography>
                      </Box>
                    )}
                    {startError && (
                      <Alert severity="error" sx={{ m: 2 }} onClose={onDismissStartError}>
                        {startError}
                      </Alert>
                    )}
                    {!selectedConversationId && !announcementFlow && (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          Sélectionnez une conversation pour afficher les messages.
                        </Typography>
                      </Box>
                    )}
                    {selectedConversationId && (
                      <>
                        {isMdDown ? (
                          <Box
                            sx={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              px: 0.5,
                              minHeight: 48,
                            }}
                          >
                            <IconButton
                              aria-label="Retour aux conversations"
                              onClick={() => onSelectConversation(null)}
                              size="small"
                              edge="start"
                            >
                              <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                              {loadingDetail ? (
                                <CircularProgress size={22} />
                              ) : detailError ? (
                                <Alert severity="error" sx={{ py: 0 }}>
                                  Conversation introuvable.
                                </Alert>
                              ) : (
                                <>
                                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                                    {detailData?.conversation?.other_user
                                      ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                                      : 'Conversation'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                    {detailData?.conversation?.announcement?.title}
                                  </Typography>
                                  {matchingInTrash && (
                                    <Chip
                                      sx={{ mt: 0.5 }}
                                      label="Matching en corbeille"
                                      color="warning"
                                      size="small"
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                            <Tooltip title="Actions">
                              <IconButton
                                size="small"
                                aria-label="Actions conversation"
                                onClick={(e) => setConversationMenuAnchor(e.currentTarget)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 2,
                              borderBottom: 1,
                              borderColor: 'divider',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            {loadingDetail ? (
                              <CircularProgress size={24} />
                            ) : detailError ? (
                              <Alert severity="error">Conversation introuvable ou accès refusé.</Alert>
                            ) : (
                              <>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    {detailData?.conversation?.other_user
                                      ? `${detailData.conversation.other_user.first_name} ${detailData.conversation.other_user.last_name}`
                                      : 'Conversation'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {detailData?.conversation?.announcement?.title}
                                  </Typography>
                                  {matchingInTrash && (
                                    <Chip
                                      sx={{ mt: 1 }}
                                      label="Matching en corbeille — messagerie limitée"
                                      color="warning"
                                      size="small"
                                    />
                                  )}
                                  {showArchivedConversations && (
                                    <Chip sx={{ mt: 1, ml: matchingInTrash ? 1 : 0 }} label="Archivée" size="small" color="secondary" />
                                  )}
                                </Box>
                                <Tooltip title="Actions">
                                  <IconButton
                                    size="small"
                                    aria-label="Actions conversation"
                                    onClick={(e) => setConversationMenuAnchor(e.currentTarget)}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        )}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: CONVERSATION_CHAT_BACKGROUND }}>
                          {shippingReadOnlyBanner}
                          {loadingDetail ? (
                            <CircularProgress />
                          ) : activeConversationTheme ? (
                            <>
                              <ConversationMessageList
                                messages={detailData?.messages || []}
                                theme={activeConversationTheme}
                                deliveryPricing={deliveryPricing}
                                deliveryLocationProviderId={deliveryLocationProviderId}
                                foundObjectStatus={foundObjectData?.status ?? null}
                                onMarkInTransit={handleMarkInTransit}
                                onMarkToShip={handleMarkToShip}
                                statusActionPending={actionRunning}
                                onEditMessage={handleEditMessage}
                                onDeleteMessage={handleDeleteMessage}
                                messageActionsPending={messageActionsPending}
                              />
                              <div ref={messagesEndRef} />
                            </>
                          ) : null}
                        </Box>
                        {showMessageComposer && (
                          <>
                        {imageUploadError && (
                          <Alert
                            severity="error"
                            onClose={() => setImageUploadError(null)}
                            sx={{ mx: 2, flexShrink: 0 }}
                          >
                            {imageUploadError}
                          </Alert>
                        )}
                        {sendTextError && (
                          <Alert
                            severity="warning"
                            onClose={() => setSendTextError(null)}
                            sx={{ mx: 2, flexShrink: 0 }}
                          >
                            {sendTextError}
                          </Alert>
                        )}
                        <Divider />
                        <input
                          ref={chatImageInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleChatImageSelected}
                        />
                        <Box
                          sx={{
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            alignItems: 'center',
                          }}
                        >
                          <IconButton
                            color="primary"
                            size="small"
                            aria-label="Envoyer une photo"
                            onClick={() => chatImageInputRef.current?.click()}
                            disabled={!canSendMessages || imageUploadPending || sendMutation.isPending}
                          >
                            {imageUploadPending ? <CircularProgress size={22} /> : <PhotoCameraIcon />}
                          </IconButton>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Écrivez votre message…"
                            value={composeText}
                            onChange={(e) => {
                              setSendTextError(null);
                              setComposeText(e.target.value);
                            }}
                            disabled={!canSendMessages || sendMutation.isPending}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                              }
                            }}
                          />
                          <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={!canSendMessages || !composeText.trim() || sendMutation.isPending}
                          >
                            <SendIcon />
                          </IconButton>
                        </Box>
                          </>
                        )}
                      </>
                    )}
                  </Card>
                </Box>
                )}

                {(isClaimed || (!hideMessagingBecauseClaimed && !isShippingLocked)) && (
                <Box
                  sx={{
                    flexShrink: 0,
                    // Toujours visible (y compris mobile sans conversation) :
                    // la restitution en main propre ne dépend pas d'un fil de discussion.
                    // Masquée pour to_ship / in_transit / disposed (pas de restitution main propre).
                    display: 'grid',
                    gridTemplateColumns: isClaimed
                      ? '1fr'
                      : 'repeat(4, minmax(0, 1fr))',
                    gap: 0.75,
                    p: { xs: 1, sm: 1.25 },
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: '#7b1fa2',
                      '&:hover': { bgcolor: '#6a1b9a' },
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={isClaimed ? () => setConfirmUndoOpen(true) : openHandoverDialog}
                    disabled={!foundObjectId || actionRunning || sendMutation.isPending}
                  >
                    {isClaimed ? 'Annuler restitution' : 'Restitué main propre'}
                  </Button>
                  {!isClaimed ? (
                    <>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="success"
                    sx={{
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={() => handleQuickSend(validationQuickMessage)}
                    disabled={!canSendMessages || actionRunning || sendMutation.isPending}
                  >
                    Je valide
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="error"
                    sx={{
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    onClick={() => handleQuickSend(rejectionQuickMessage)}
                    disabled={!canSendMessages || actionRunning || sendMutation.isPending}
                  >
                    Je ne valide pas
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: '#1976d2',
                      py: { xs: 0.75, sm: 1 },
                      px: 0.5,
                      minWidth: 0,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform: 'none',
                      whiteSpace: 'normal',
                    }}
                    component={colissimoEtiquetteUrl ? 'a' : 'button'}
                    href={colissimoEtiquetteUrl || undefined}
                    target={colissimoEtiquetteUrl ? '_blank' : undefined}
                    rel={colissimoEtiquetteUrl ? 'noreferrer' : undefined}
                    onClick={colissimoEtiquetteUrl ? undefined : () => void handleDeliveryOfferAction()}
                    disabled={
                      !canSendMessages ||
                      actionRunning ||
                      sendMutation.isPending ||
                      (deliveryOfferAlreadySent && !colissimoEtiquetteUrl)
                    }
                  >
                    {colissimoEtiquetteUrl
                      ? 'Imprimer le bon'
                      : deliveryOfferAlreadySent
                        ? 'Bon envoyé'
                        : 'Bon de livraison'}
                  </Button>
                    </>
                  ) : null}
                </Box>
                )}
              </>
            )}

            {!isMdDown && detailMainTab === 1 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Modifiez les informations de l&apos;objet (photos, lieu, attributs…).
                </Typography>
                <Button variant="contained" onClick={onRequestEditFoundObject} disabled={!onRequestEditFoundObject}>
                  Ouvrir le formulaire de modification
                </Button>
              </Box>
            )}

            {!isMdDown && detailMainTab === 2 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {foundObjectData?.storage_box ? (
                  <>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {foundObjectData.storage_box.name}
                    </Typography>
                    {foundObjectData.storage_box.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {foundObjectData.storage_box.description}
                      </Typography>
                    )}
                    {foundObjectData.storage_box.provider_item && (
                      <Chip
                        size="small"
                        label={foundObjectData.storage_box.provider_item.name}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune boîte de stockage associée. Assignez-en une depuis le formulaire de modification.
                  </Typography>
                )}
              </Box>
            )}

            {!isMdDown && detailMainTab === 3 && (
              <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Statut envoi :{' '}
                  <strong>
                    {foundObjectData?.status === 'to_ship'
                      ? 'À expédier'
                      : foundObjectData?.status === 'in_transit'
                        ? 'Parti en livraison'
                        : foundObjectData?.status === 'claimed'
                          ? 'Restitué'
                          : foundObjectData?.status === 'found'
                            ? 'Trouvé'
                            : foundObjectData?.status || '—'}
                  </strong>
                </Typography>
                {colissimoEtiquetteUrl ? (
                  <Button variant="contained" href={colissimoEtiquetteUrl} target="_blank" rel="noreferrer">
                    Ouvrir l&apos;étiquette / bon d&apos;envoi
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune étiquette Colissimo détectée dans la conversation. Utilisez « Envoyer bon de livraison »
                    dans l&apos;onglet Discuter pour lancer la procédure.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      )}

      <Dialog open={confirmUndoOpen} onClose={() => setConfirmUndoOpen(false)}>
        <DialogTitle>Annuler la restitution ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action remettra l'objet en statut "Trouvé". Voulez-vous continuer ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUndoOpen(false)} disabled={actionRunning}>
            Annuler
          </Button>
          <Button onClick={handleUndoClaimed} color="warning" variant="contained" disabled={actionRunning}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deliveryOfferDialogOpen}
        onClose={() => setDeliveryOfferDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Proposer une livraison</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Choisissez le transporteur et le tarif pour cette proposition uniquement (sans modifier le tarif
            général admin). Le propriétaire paiera ce montant.
          </Typography>
          <ChooseDeliveryCarrierPanel
            value={deliveryOfferCarrier}
            onChange={handleDeliveryOfferCarrierChange}
            pricing={deliveryPricing}
            locationProviderId={deliveryLocationProviderId}
            disabled={actionRunning || sendMutation.isPending}
          />
          <TextField
            label="Tarif proposé (€)"
            value={deliveryOfferAmountEur}
            onChange={(e) => {
              setDeliveryOfferAmountEur(e.target.value);
              setDeliveryOfferAmountError(null);
            }}
            error={!!deliveryOfferAmountError}
            helperText={
              deliveryOfferAmountError
              || 'Prérempli avec le tarif admin. Modifiez-le pour un objet plus lourd, fragile, etc.'
            }
            disabled={actionRunning || sendMutation.isPending}
            inputProps={{ inputMode: 'decimal', min: 1, step: 0.5 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            sx={{ maxWidth: 220 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryOfferDialogOpen(false)} disabled={actionRunning}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => void sendDeliveryOffer()}
            disabled={actionRunning || sendMutation.isPending || !deliveryOfferCarrier}
          >
            Proposer ce transporteur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmHandoverOpen}
        onClose={closeHandoverDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 1, px: 3 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: HANDOVER_DIALOG_GREEN_SOFT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <EmailIcon sx={{ fontSize: 36, color: HANDOVER_DIALOG_GREEN }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Pour la restitution de l&apos;objet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Entrer le email du propriétaire de l&apos;objet !
          </Typography>
          <TextField
            placeholder="Email du propriétaire"
            type="email"
            fullWidth
            value={handoverEmail}
            onChange={(e) => setHandoverEmail(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: HANDOVER_DIALOG_GREEN, fontSize: 22 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: HANDOVER_DIALOG_GREEN, borderWidth: 2 },
                '&:hover fieldset': { borderColor: HANDOVER_DIALOG_GREEN },
                '&.Mui-focused fieldset': { borderColor: HANDOVER_DIALOG_GREEN },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, pt: 2 }}>
          <Button
            onClick={closeHandoverDialog}
            disabled={actionRunning}
            variant="outlined"
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1.1,
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={async () => {
              setConfirmHandoverOpen(false);
              await handleMarkClaimed();
              setHandoverEmail('');
            }}
            variant="contained"
            disabled={actionRunning}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1.1,
              bgcolor: HANDOVER_DIALOG_GREEN,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1b5e20', boxShadow: 'none' },
            }}
          >
            Restituer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={objectPhotoFullscreen && Boolean(objectFullImageUrl)}
        onClose={() => setObjectPhotoFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.94)',
            m: 0,
            borderRadius: 0,
          },
        }}
      >
        <IconButton
          onClick={() => setObjectPhotoFullscreen(false)}
          aria-label="Fermer la photo"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.12)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={() => setObjectPhotoFullscreen(false)}
        >
          {objectFullImageUrl ? (
            <Box
              component="img"
              src={objectFullImageUrl}
              alt="Photo de l'objet"
              onClick={(event) => event.stopPropagation()}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : null}
        </Box>
      </Dialog>

      <Menu
        anchorEl={conversationMenuAnchor}
        open={Boolean(conversationMenuAnchor) && !!selectedConversationId}
        onClose={() => setConversationMenuAnchor(null)}
      >
        <MenuItem
          disabled={!selectedConversationId || markUnreadMutation.isPending}
          onClick={() => selectedConversationId && markUnreadMutation.mutate(selectedConversationId)}
        >
          <ListItemIcon>
            <MarkEmailUnreadIcon fontSize="small" />
          </ListItemIcon>
          Marquer comme non lu
        </MenuItem>
        <MenuItem
          disabled={!selectedConversationId || archiveMutation.isPending}
          onClick={() => selectedConversationId && archiveMutation.mutate(selectedConversationId)}
        >
          <ListItemIcon>
            {showArchivedConversations ? (
              <UnarchiveIcon fontSize="small" />
            ) : (
              <ArchiveIcon fontSize="small" />
            )}
          </ListItemIcon>
          {showArchivedConversations ? 'Désarchiver' : 'Archiver'}
        </MenuItem>
      </Menu>

      <Snackbar
        open={actionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={actionSnackbar.severity}
          onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {actionSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FoundObjectMessagesWorkspace;
